"use client";

import React, { useMemo, createContext, useContext } from "react";

interface MockRouterContextValue {
  navigate: (to: string, options?: { replace?: boolean }) => void;
  location: {
    pathname: string;
  };
  searchParams: URLSearchParams;
}

const MockRouterContext = createContext<MockRouterContextValue | null>(null);

interface MockRouterProviderProps {
  children: React.ReactNode;
  pathname?: string;
  searchParams?: URLSearchParams;
}

/**
 * MockRouterProvider
 *
 * Provides mock React Router context for components rendered in the webbuilder canvas preview.
 * This prevents navigation errors when sections use React Router hooks
 * (useNavigate, useLocation, useSearchParams) in preview mode.
 *
 * This replaces the previous Next.js router mock with React Router equivalents.
 */
export const MockRouterProvider: React.FC<MockRouterProviderProps> = ({
  children,
  pathname = "/preview",
  searchParams,
}) => {
  // Create a mock router object that satisfies React Router expectations
  const mockRouterValue = useMemo<MockRouterContextValue>(() => ({
    navigate: (to: string, options?: { replace?: boolean }) => {
      console.log("[Preview] Navigation prevented:", to, options);
    },
    location: {
      pathname,
    },
    searchParams: searchParams || new URLSearchParams(),
  }), [pathname, searchParams]);

  return (
    <MockRouterContext.Provider value={mockRouterValue}>
      {children}
    </MockRouterContext.Provider>
  );
};

// Hook to use the mock router context
export const useMockRouter = () => {
  const context = useContext(MockRouterContext);
  if (!context) {
    // Return safe defaults if not in preview mode
    return {
      navigate: () => {},
      location: { pathname: "/" },
      searchParams: new URLSearchParams(),
    };
  }
  return context;
};

// Backwards compatibility export
export const MockNextRouterProvider = MockRouterProvider;

export default MockRouterProvider;
